import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Start Your Application' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

export function ApplyNow() {
  const applyCta = useSlotGroup('applynow.apply_cta', applyCtaDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="an-section">
      <div ref={sectionRef} className="reveal-fade-up an-container">
        <EditableToken path="applynow.heading" as="h2" className="sr-only" />
        <div className="an-cta-wrap">
          {applyCta.url ? (
            <EditableSlotGroup groupPath="applynow.apply_cta" definition={applyCtaDef}>
              <a
                href={applyCta.url}
                target={applyCta._target || '_blank'}
                rel={applyCta._target === '_self' ? undefined : 'noopener noreferrer'}
                className="an-btn"
              >
                {applyCta.label || 'Start Your Application'}
              </a>
            </EditableSlotGroup>
          ) : (
            <span className="an-btn an-btn--disabled">Application URL Not Available</span>
          )}
        </div>
      </div>

      <style>{`
        .an-section {
          padding: 3.5rem 0;
          background: white;
        }
        @media (min-width: 768px) {
          .an-section { padding: 4.75rem 0; }
        }
        .an-container {
          max-width: 56rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .an-container { padding: 0 1.5rem; } }

        .an-cta-wrap {
          display: flex;
          justify-content: center;
        }

        .an-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 2rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #fff);
          background-color: var(--vibe-color-primary);
          text-decoration: none;
          cursor: pointer;
          transition: opacity 0.25s ease;
        }
        .an-btn:hover {
          opacity: 0.85;
        }

        .an-btn--disabled,
        .an-btn--disabled:hover {
          background: transparent;
          color: var(--vibe-color-text-muted);
          border: 1.5px solid var(--vibe-color-border, #e5e7eb);
          cursor: default;
          opacity: 0.7;
          transform: none;
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ApplyNow',
  displayName: 'Apply Now',
  tokens: {
    'applynow.heading': {
      label: 'Section Heading',
      type: 'text',
      defaultValue: 'Apply Now',
    },
  },
  slotGroups: {
    'applynow.apply_cta': applyCtaDef,
  },
};

(ApplyNow as any).contentSchema = contentSchema;

export default ApplyNow;
